import { Wallet, Gem, Home, Car, Briefcase, Crosshair, Speaker, Palette } from 'lucide-react';

// Asset type configurations - shared across all modals
export const ASSET_CONFIGS = {
  ART: {
    label: 'Art',
    icon: Palette,
    color: 'rose',
    fields: [
      { key: 'symbol', label: 'Artwork Name', type: 'text', required: true },
      { key: 'artArtist', label: 'Artist', type: 'text', required: false },
      { key: 'artMedium', label: 'Medium', type: 'select', required: false, options: ['Oil Painting', 'Acrylic', 'Watercolor', 'Sculpture', 'Photography', 'Print', 'Mixed Media', 'Digital', 'Other'] },
      { key: 'artYear', label: 'Year Created', type: 'text', required: false },
      { key: 'artDimensions', label: 'Dimensions', type: 'text', required: false, placeholder: 'e.g. 24" x 36"' },
      { key: 'serialNumber', label: 'Edition/Serial', type: 'text', required: false },
      { key: 'condition', label: 'Condition', type: 'select', required: false, options: ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'] },
      { key: 'purchaseDate', label: 'Purchase Date', type: 'date', required: false },
      { key: 'costBasis', label: 'Purchase Price', type: 'number', required: true, isCurrency: true },
      { key: 'purchaseLocation', label: 'Where Purchased', type: 'text', required: false },
      { key: 'platform', label: 'Storage Location', type: 'text', required: false },
      { key: 'shares', label: 'Current Value', type: 'number', required: true, isCurrency: true },
      { key: 'insuredBy', label: 'Insured By', type: 'text', required: false },
      { key: 'notes', label: 'Notes', type: 'textarea', required: false },
    ],
    getDisplayName: (asset) => {
      if (asset.artArtist || asset.artist) {
        return `${asset.symbol} by ${asset.artArtist || asset.artist}`;
      }
      return asset.symbol || asset.name || 'Artwork';
    },
    showExpandedDetails: true
  },
  CASH: {
    label: 'Cash',
    icon: Wallet,
    color: 'emerald',
    fields: [
      { key: 'symbol', label: 'Account Name', type: 'text', required: true },
      { key: 'platform', label: 'Bank/Institution', type: 'text', required: true },
      { key: 'accountType', label: 'Account Type', type: 'select', required: false, options: ['Checking', 'Savings', 'Money Market', 'CD', 'Brokerage Cash', 'Other'] },
      { key: 'accountNumber', label: 'Account # (last 4)', type: 'text', required: false },
      { key: 'shares', label: 'Balance', type: 'number', required: true, isCurrency: true },
    ]
  },
  JEWELRY: {
    label: 'Jewelry/Collectibles',
    icon: Gem,
    color: 'purple',
    fields: [
      { key: 'symbol', label: 'Item Name', type: 'text', required: true },
      { key: 'jewelryType', label: 'Type', type: 'select', required: true, options: ['Watch', 'Necklace', 'Bracelet', 'Ring', 'Earrings', 'Collectible', 'Art', 'Other'] },
      { key: 'brand', label: 'Brand', type: 'text', required: false },
      { key: 'model', label: 'Model', type: 'text', required: false },
      { key: 'serialNumber', label: 'Serial Number', type: 'text', required: false },
      { key: 'condition', label: 'Current Condition', type: 'select', required: false, options: ['New', 'Excellent', 'Very Good', 'Good', 'Fair', 'Poor'] },
      { key: 'originalPackaging', label: 'Original Packaging', type: 'select', required: false, options: ['Yes', 'No'] },
      { key: 'platform', label: 'Storage Location', type: 'text', required: false },
      { key: 'insuredBy', label: 'Insured By', type: 'text', required: false },
      { key: 'costBasis', label: 'Purchase Price', type: 'number', required: true, isCurrency: true },
      { key: 'shares', label: 'Appraised Value', type: 'number', required: true, isCurrency: true },
      { key: 'purchaseDate', label: 'When Purchased', type: 'text', required: false, placeholder: 'e.g. 2020 or Jan 2020' },
      { key: 'purchaseLocation', label: 'Where Purchased', type: 'text', required: false },
      { key: 'purchaseCondition', label: 'Purchase Condition', type: 'select', required: false, options: ['New', 'Like New', 'Pre-owned', 'Vintage', 'Estate'] },
    ]
  },
  'REAL ESTATE': {
    label: 'Real Estate',
    icon: Home,
    color: 'blue',
    fields: [
      { key: 'symbol', label: 'Property Name', type: 'text', required: true },
      { key: 'name', label: 'Address', type: 'text', required: false },
      { key: 'platform', label: 'Property Type', type: 'select', required: true, options: ['Primary Residence', 'Rental Property', 'Vacation Home', 'Commercial', 'Land', 'Other'] },
      { key: 'costBasis', label: 'Purchase Price', type: 'number', required: true, isCurrency: true },
      { key: 'shares', label: 'Current Value', type: 'number', required: true, isCurrency: true },
    ]
  },
  VEHICLES: {
    label: 'Vehicles',
    icon: Car,
    color: 'orange',
    fields: [
      { key: 'vehicleYear', label: 'Year', type: 'number', required: true, isYear: true },
      { key: 'vehicleMake', label: 'Make', type: 'text', required: true },
      { key: 'vehicleModel', label: 'Model', type: 'text', required: true },
      { key: 'vin', label: 'VIN', type: 'text', required: false },
      { key: 'licensePlate', label: 'License Plate', type: 'text', required: false },
      { key: 'platform', label: 'Where Purchased', type: 'text', required: false },
      { key: 'costBasis', label: 'Purchase Price', type: 'number', required: true, isCurrency: true },
      { key: 'shares', label: 'Current Value', type: 'number', required: true, isCurrency: true },
      { key: 'insuredBy', label: 'Insured By', type: 'text', required: false },
      { key: 'sirius', label: 'Entertainment System', type: 'text', required: false },
      { key: 'mileage', label: 'Last Known Mileage', type: 'number', required: false },
      { key: 'mileageDate', label: 'Mileage Update Date', type: 'date', required: false },
      { key: 'notes', label: 'Notes', type: 'textarea', required: false },
    ],
    getDisplayName: (asset) => {
      if (asset.vehicleYear && asset.vehicleMake && asset.vehicleModel) {
        return `${asset.vehicleYear} ${asset.vehicleMake} ${asset.vehicleModel}`;
      }
      return asset.symbol || asset.name || 'Vehicle';
    },
    showExpandedDetails: true
  },
  FIREARMS: {
    label: 'Guns/Weapons',
    icon: Crosshair,
    color: 'slate',
    fields: [
      { key: 'symbol', label: 'Item Name', type: 'text', required: true },
      { key: 'gunManufacturer', label: 'Manufacturer', type: 'text', required: false },
      { key: 'gunModel', label: 'Model', type: 'text', required: false },
      { key: 'serialNumber', label: 'Serial Number', type: 'text', required: false },
      { key: 'gunType', label: 'Firearm Type', type: 'select', required: false, options: ['Handgun', 'Rifle', 'Shotgun', 'Other'] },
      { key: 'gunAction', label: 'Action', type: 'select', required: false, options: ['Semi-auto', 'Bolt', 'Revolver', 'Pump', 'Lever', 'Break', 'Single-shot', 'Full-auto', 'Other'] },
      { key: 'gunCaliber', label: 'Caliber/Gauge', type: 'text', required: false },
      { key: 'gunCountry', label: 'Country of Origin', type: 'text', required: false },
      { key: 'condition', label: 'Condition', type: 'select', required: false, options: ['New', 'Excellent', 'Very Good', 'Good', 'Fair', 'Poor'] },
      { key: 'purchaseDate', label: 'Purchase Date', type: 'date', required: false },
      { key: 'costBasis', label: 'Purchase Price', type: 'number', required: true, isCurrency: true },
      { key: 'purchaseLocation', label: 'Where Purchased', type: 'select', required: false, options: ['Dealer', 'Private Sale', 'Auction', 'Gun Show', 'Online', 'Inherited', 'Gift', 'Other'] },
      { key: 'platform', label: 'Storage Location', type: 'text', required: false },
      { key: 'shares', label: 'Current Value', type: 'number', required: true, isCurrency: true },
      { key: 'appraisedValue', label: 'Appraised Value', type: 'number', required: false, isCurrency: true },
      { key: 'lastValuationDate', label: 'Last Valuation Date', type: 'date', required: false },
      { key: 'isInsured', label: 'Insured?', type: 'select', required: false, options: ['Yes', 'No'] },
      { key: 'insuredBy', label: 'Insured By', type: 'text', required: false },
      { key: 'notes', label: 'Notes', type: 'textarea', required: false },
    ],
    getDisplayName: (asset) => {
      if (asset.gunManufacturer && asset.gunModel) {
        return `${asset.gunManufacturer} ${asset.gunModel}`;
      }
      return asset.symbol || asset.name || 'Firearm';
    },
    showExpandedDetails: true
  },
  'PRIVATE INVESTMENTS': {
    label: 'Private Investments/Loans',
    icon: Briefcase,
    color: 'indigo',
    fields: [
      { key: 'symbol', label: 'Company/Investment Name', type: 'text', required: true },
      { key: 'platform', label: 'Investment Type', type: 'select', required: true, options: ['Private Equity', 'Startup', 'Partnership', 'Syndication', 'Loan', 'Note', 'Promissory Note', 'Other'] },
      { key: 'investmentStatus', label: 'Status', type: 'select', required: false, options: ['Active', 'Matured', 'Defaulted', 'Exited', 'Pending'] },
      { key: 'contactPerson', label: 'Contact Person', type: 'text', required: false },
      { key: 'interestRate', label: 'Interest Rate (%)', type: 'text', required: false, placeholder: 'e.g. 8.5' },
      { key: 'investmentDate', label: 'Investment Date', type: 'date', required: false },
      { key: 'maturityDate', label: 'Maturity Date', type: 'date', required: false },
      { key: 'costBasis', label: 'Investment Amount', type: 'number', required: true, isCurrency: true },
      { key: 'shares', label: 'Current/Estimated Value', type: 'number', required: true, isCurrency: true },
      { key: 'terms', label: 'Terms', type: 'textarea', required: false, placeholder: 'Payment schedule, conditions, etc.' },
      { key: 'notes', label: 'Notes', type: 'textarea', required: false },
    ],
    getDisplayName: (asset) => {
      return asset.symbol || asset.name || 'Investment';
    },
    showExpandedDetails: true
  },
  ELECTRONICS: {
    label: 'Electronics/Stereo',
    icon: Speaker,
    color: 'cyan',
    fields: [
      { key: 'symbol', label: 'Item Name', type: 'text', required: true },
      { key: 'electronicsBrand', label: 'Brand', type: 'text', required: false },
      { key: 'electronicsModel', label: 'Model', type: 'text', required: false },
      { key: 'serialNumber', label: 'Serial Number', type: 'text', required: false },
      { key: 'electronicsType', label: 'Type', type: 'select', required: false, options: ['Speaker', 'Amplifier', 'Receiver', 'Turntable', 'TV', 'Computer', 'Camera', 'Headphones', 'Home Theater', 'Vintage Audio', 'Other'] },
      { key: 'condition', label: 'Condition', type: 'select', required: false, options: ['New', 'Excellent', 'Very Good', 'Good', 'Fair', 'Poor'] },
      { key: 'purchaseDate', label: 'Purchase Date', type: 'date', required: false },
      { key: 'costBasis', label: 'Purchase Price', type: 'number', required: true, isCurrency: true },
      { key: 'purchaseLocation', label: 'Where Purchased', type: 'text', required: false },
      { key: 'platform', label: 'Storage Location', type: 'text', required: false },
      { key: 'shares', label: 'Current Value', type: 'number', required: true, isCurrency: true },
      { key: 'insuredBy', label: 'Insured By', type: 'text', required: false },
      { key: 'notes', label: 'Notes', type: 'textarea', required: false },
    ],
    getDisplayName: (asset) => {
      if (asset.electronicsBrand && asset.electronicsModel) {
        return `${asset.electronicsBrand} ${asset.electronicsModel}`;
      }
      return asset.symbol || asset.name || 'Electronics';
    },
    showExpandedDetails: true
  }
};

// Helper function to get actual value from non-market assets
export const getActualValue = (item) => {
  if (item.currentPrice && item.currentPrice > 0) return item.currentPrice;
  if (item.shares && item.shares > 1) return item.shares;
  return item.currentPrice || 0;
};
